import React, { useState, useEffect } from 'react';
import ExpiredListingNotice from '@/components/ExpiredListingNotice';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import axios from 'axios';
import { CURRENCIES_LIST, ccySym as currencySymbol } from '@/utils/currencyUtils';
import {
  Package, Calendar, Weight, ArrowRight, Star, MessageSquare, Send,
  Ban, Copy, AlertTriangle, AlertCircle, Clock, Pencil, ArrowLeft, CheckCircle2,
  ShieldCheck, Trash2, Globe,
} from 'lucide-react';
import ProposeModal from '@/components/ProposeModal';
import { isNegotiable } from '@/utils/propositionPricing';
import ConfirmDialog from '@/components/ConfirmDialog';
import { cityFlag as flag } from '@/utils/cityFlag';
import { BACKEND_URL } from '@/config';
import VerificationBadges from '@/components/VerificationBadges';

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

const STATUS = {
  DRAFT:          { label: 'Brouillon',    tone: 'gray' },
  PENDING_REVIEW: { label: 'En attente',   tone: 'blue' },
  PUBLISHED:      { label: 'Publié',       tone: 'teal' },
  ASSIGNED:       { label: 'Assigné',      tone: 'teal' },
  IN_PROGRESS:    { label: 'Pris en charge', tone: 'gold' },
  IN_TRANSIT:     { label: 'En transport',  tone: 'gold' },
  DELIVERED:      { label: 'Livré',         tone: 'teal' },
  COMPLETED:      { label: 'Livré',         tone: 'teal' },
  DISPUTED:       { label: 'Litige',       tone: 'red' },
  ARCHIVED:       { label: 'Archivé',      tone: 'gray' },
  REJECTED:       { label: 'Rejeté',       tone: 'red' },
  CANCELLED:      { label: 'Annulée',      tone: 'red' },
  EXPIRED:        { label: 'Expirée',      tone: 'gray' },
};

const CONSTRAINT_META = {
  fragile:     { icon: '🏺', label: 'Fragile' },
  very_fragile:{ icon: '🔮', label: 'Très fragile' },
  upright:     { icon: '⬆️', label: 'Garder à la verticale' },
  no_stack:    { icon: '🚫', label: 'Ne pas empiler' },
  temp:        { icon: '🌡️', label: 'Température contrôlée' },
  cold_chain:  { icon: '❄️', label: 'Chaîne du froid' },
  perishable:  { icon: '🥗', label: 'Produit périssable' },
  electronic:  { icon: '💻', label: 'Produit électronique' },
  valuable:    { icon: '💎', label: 'Produit de valeur' },
  urgent:      { icon: '🚀', label: 'Livraison urgente' },
  signature:   { icon: '✍️', label: 'Signature obligatoire' },
  insurance:   { icon: '🛡️', label: 'Assurance recommandée' },
  bulky:       { icon: '📦', label: 'Colis volumineux' },
  careful:     { icon: '🤲', label: 'Manipulation avec précaution' },
  other:       { icon: '📝', label: 'Autre' },
};

const CARRIER_PREF_META = {
  verified:     '✅ Transporteur vérifié',
  professional: '🏢 Transporteur entreprise',
  particular:   '👤 Transporteur particulier',
  fast:         '🚀 Livraison rapide',
  direct:       '🎯 Transport direct',
  no_stop:      '✈️ Sans escale',
  weekend:      '📅 Disponible le week-end',
  evening:      '🌙 Disponible en soirée',
  ac_vehicle:   '❄️ Véhicule climatisé',
  insured:      '🛡️ Assurance incluse',
  fragile_ok:   '🏺 Accepte les colis fragiles',
  bulky_ok:     '📦 Accepte les colis volumineux',
  french:       '🇫🇷 Communication en français',
  english:      '🇬🇧 Communication en anglais',
  experienced:  '⭐ Expérience confirmée',
};


function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
}
function formatDateTime(d) {
  if (!d) return '·';
  return new Date(d).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function ShipmentDetailPage() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposeMode, setProposeMode] = useState('direct');
  const [confirmDlg, setConfirmDlg] = useState(null); // { action: 'cancel'|'delete' }

  const load = () => {
    setLoading(true);
    axios.get(`${API}/shipments/${id}`)
      .then(r => setShipment(r.data))
      .catch(() => toast.error('Annonce introuvable'))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (loading) return <div style={{ maxWidth: 720, margin: '0 auto', padding: 24 }}><div className="skeleton" style={{ height: 320, borderRadius: 14 }} /></div>;
  if (!shipment) return <div className="empty-state"><div className="empty-state-title">Annonce introuvable</div></div>;

  const isOwner = shipment.user_id === user?.id;
  const status = STATUS[shipment.status] || { label: shipment.status, tone: 'gray' };
  const moderation = shipment.moderation || {};
  const constraints    = Array.isArray(shipment.constraints)         ? shipment.constraints         : [];
  const carrierPrefs   = Array.isArray(shipment.carrier_preferences) ? shipment.carrier_preferences : [];
  const _rawCats       = Array.isArray(shipment.categories)          ? shipment.categories          : [];
  const categories     = _rawCats.length > 1 ? _rawCats.filter(c => c !== 'Autre') : _rawCats;
  const currencies     = Array.isArray(shipment.currencies)          ? shipment.currencies          : [];
  const primaryCcy     = shipment.primary_currency || 'EUR';
  const priceSymbol    = currencySymbol(primaryCcy);
  const isUrgent       = constraints.includes('urgent');

  const handleCancel = () => setConfirmDlg({ action: 'cancel' });

  const doCancel = async () => {
    setConfirmDlg(null);
    try {
      await axios.post(`${API}/shipments/${id}/cancel`);
      toast.success('Annonce annulée.');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = () => setConfirmDlg({ action: 'delete' });

  const doDelete = async () => {
    setConfirmDlg(null);
    try {
      await axios.delete(`${API}/shipments/${id}`);
      toast.success('Annonce supprimée.');
      navigate('/app/shipments');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur lors de la suppression'); }
  };

  const handleRepublish = () => {
    sessionStorage.setItem('c2s_shipment_duplicate', JSON.stringify({
      title: shipment.title, description: shipment.description,
      departure_city: shipment.departure_city, arrival_city: shipment.arrival_city,
      departure_country: shipment.departure_country || '',
      arrival_country: shipment.arrival_country || '',
      weight_kg: shipment.weight_kg, dimensions: shipment.dimensions,
      price_offered: shipment.price_offered,
      primary_currency: shipment.primary_currency || 'EUR',
      currencies: shipment.currencies || ['EUR'],
      categories: shipment.categories || [],
      category_custom: shipment.category_custom || '',
      constraints: shipment.constraints || [],
      constraint_custom: shipment.constraint_custom || '',
      carrier_preferences: shipment.carrier_preferences || [],
      message_to_carriers: shipment.message_to_carriers || '',
    }));
    toast.success('Données copiées — adaptez si besoin avant de republier.');
    navigate('/app/shipments/new?duplicate=1');
  };

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <button type="button" className="mlc-btn mlc-btn--ghost" style={{ marginBottom: 18, height: 32, fontSize: 12 }} onClick={() => navigate(-1)} data-testid="btn-back">
        <ArrowLeft size={14} /> Retour
      </button>

      {shipment.status === 'REJECTED' && (
        <div className="detail-banner detail-banner--red" data-testid="banner-rejected">
          <div className="detail-banner-icon"><AlertCircle size={20} /></div>
          <div className="detail-banner-body">
            <div className="detail-banner-title">Annonce refusée par la modération</div>
            {moderation.reason && <div className="detail-banner-text"><strong>Motif&nbsp;:</strong> {moderation.reason}</div>}
            {moderation.rejected_at && <div className="detail-banner-meta">Refusée le {formatDateTime(moderation.rejected_at)}</div>}
            {isOwner && (
              <button type="button" className="mlc-btn mlc-btn--primary" style={{ height: 32, fontSize: 12, marginTop: 10 }} onClick={handleRepublish} data-testid="btn-republish">
                <Copy size={14} /> Modifier et republier
              </button>
            )}
          </div>
        </div>
      )}
      {shipment.status === 'PENDING_REVIEW' && (
        <div className="detail-banner detail-banner--blue" data-testid="banner-pending">
          <div className="detail-banner-icon"><Clock size={20} /></div>
          <div className="detail-banner-body">
            <div className="detail-banner-title">En attente de validation</div>
            <div className="detail-banner-text">Votre annonce sera vérifiée par notre équipe avant publication. Cela prend généralement quelques heures.</div>
          </div>
        </div>
      )}
      {shipment.status === 'EXPIRED' && (
        <ExpiredListingNotice kind="colis" date={shipment.departure_date} editPath={`/app/shipments/${id}/edit`}
          isOwner={isOwner} onDuplicate={handleRepublish} />
      )}
      {shipment.status === 'CANCELLED' && (
        <div className="detail-banner detail-banner--gray" data-testid="banner-cancelled">
          <div className="detail-banner-icon"><Ban size={20} /></div>
          <div className="detail-banner-body">
            <div className="detail-banner-title">Annonce annulée</div>
            <div className="detail-banner-text">Cette annonce n'apparaît plus sur la marketplace.</div>
            {isOwner && (
              <button type="button" className="mlc-btn mlc-btn--ghost" style={{ height: 32, fontSize: 12, marginTop: 10 }} onClick={handleRepublish} data-testid="btn-duplicate">
                <Copy size={14} /> Dupliquer
              </button>
            )}
          </div>
        </div>
      )}
      {['DELIVERED','COMPLETED'].includes(shipment.status) && (
        <div className="detail-banner detail-banner--teal" data-testid="banner-completed">
          <div className="detail-banner-icon"><CheckCircle2 size={20} /></div>
          <div className="detail-banner-body">
            <div className="detail-banner-title">Mission terminée</div>
            <div className="detail-banner-text">Le colis a été livré avec succès.</div>
          </div>
        </div>
      )}

      <div className="detail-card">
        <div className="detail-head">
          <div className="detail-head-info">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
              <span className={`mlc-badge mlc-badge--${status.tone}`} data-testid="status-pill">{status.label}</span>
              {isUrgent && <span className="mlc-badge mlc-badge--red">🚀 Urgent</span>}
            </div>
            <h1 className="detail-title" data-testid="shipment-title">{shipment.title}</h1>
            <div className="detail-route">
              <span>{flag(shipment.departure_city)} {shipment.departure_city}</span>
              <ArrowRight size={14} className="detail-route-arrow" />
              <span>{flag(shipment.arrival_city)} {shipment.arrival_city}</span>
            </div>
          </div>
          <div className="detail-price">
            <div className="detail-price-amount">{shipment.price_offered ? <>{shipment.price_offered.toFixed(0)}<span className="detail-price-currency">{priceSymbol}</span></> : 'Budget libre'}</div>
            <div className="detail-price-label">Budget proposé</div>
          </div>
        </div>

        {/* Infos principales */}
        <div className="detail-grid">
          <InfoBlock icon={<Calendar size={15} />} label="Date souhaitée" value={formatDate(shipment.departure_date)} />
          <InfoBlock icon={<Weight size={15} />}   label="Poids"          value={`${shipment.weight_kg} kg`} />
          {shipment.dimensions && <InfoBlock icon={<Package size={15} />} label="Dimensions" value={shipment.dimensions} />}
          {shipment.departure_country && <InfoBlock icon={<Globe size={15} />} label="Pays départ"  value={shipment.departure_country} />}
          {shipment.arrival_country   && <InfoBlock icon={<Globe size={15} />} label="Pays arrivée" value={shipment.arrival_country} />}
          {currencies.length > 0 && (
            <InfoBlock icon={<ShieldCheck size={15} />} label="Devises acceptées"
              value={currencies.map(c => c === primaryCcy ? `★ ${c}` : c).join(', ')} />
          )}
          {Array.isArray(shipment.photos) && shipment.photos.length > 0 && (
            <InfoBlock icon={<Package size={15} />} label="Photos" value={`${shipment.photos.length} photo${shipment.photos.length>1?'s':''}`} />
          )}
        </div>

        {shipment.description && (
          <div className="detail-section">
            <div className="detail-section-label">Description</div>
            <div className="detail-section-text">{shipment.description}</div>
          </div>
        )}

        {/* Catégories */}
        {categories.length > 0 && (
          <div className="detail-section">
            <div className="detail-section-label">Catégories</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
              {categories.map(cat => (
                <span key={cat} className="mlc-badge mlc-badge--blue" style={{ fontSize: 12 }}>{cat}</span>
              ))}
              {shipment.category_custom && (
                <span className="mlc-badge mlc-badge--blue" style={{ fontSize: 12 }}>📝 {shipment.category_custom}</span>
              )}
            </div>
          </div>
        )}

        {/* Contraintes de transport */}
        {(constraints.length > 0 || shipment.constraint_custom) && (
          <div className="detail-section">
            <div className="detail-section-label">Contraintes de transport</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
              {constraints.filter(id => id !== 'other').map(id => {
                const m = CONSTRAINT_META[id];
                return m ? (
                  <span key={id} className="mlc-badge mlc-badge--gray" style={{ fontSize: 12 }}>
                    {m.icon} {m.label}
                  </span>
                ) : null;
              })}
              {shipment.constraint_custom && (
                <span className="mlc-badge mlc-badge--gray" style={{ fontSize: 12 }}>📋 {shipment.constraint_custom}</span>
              )}
            </div>
          </div>
        )}

        {/* Préférences transporteur */}
        {(carrierPrefs.length > 0 || shipment.message_to_carriers) && (
          <div className="detail-section">
            <div className="detail-section-label">Préférences transporteur</div>
            {carrierPrefs.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {carrierPrefs.map(id => {
                  const label = CARRIER_PREF_META[id];
                  return label ? (
                    <span key={id} className="mlc-badge mlc-badge--teal" style={{ fontSize: 12 }}>{label}</span>
                  ) : null;
                })}
              </div>
            )}
            {shipment.message_to_carriers && (
              <div className="detail-section-text" style={{ marginTop: 10, fontStyle: 'italic', color: 'var(--ink2)' }}>
                « {shipment.message_to_carriers} »
              </div>
            )}
          </div>
        )}

        {/* Photos */}
        {Array.isArray(shipment.photos) && shipment.photos.length > 0 && (
          <div className="detail-section">
            <div className="detail-section-label">Photos ({shipment.photos.length})</div>
            <div className="detail-photos">
              {shipment.photos.map((p, i) => <img key={i} src={p} alt={`photo-${i}`} loading="lazy" />)}
            </div>
          </div>
        )}

        {shipment.owner && (
          <div className="detail-owner">
            <div className="av av-teal" style={{ width: 44, height: 44, fontSize: 14 }}>{(shipment.owner.first_name || '?')[0]}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="detail-owner-name">{shipment.owner.first_name} {shipment.owner.last_name}</div>
              <div className="detail-owner-meta">
                {shipment.owner.avg_rating && <span><Star size={11} fill="var(--gold)" color="var(--gold)" /> {shipment.owner.avg_rating} ({shipment.owner.rating_count || 0})</span>}
                <VerificationBadges user={shipment.owner} />
              </div>
            </div>
            {!isOwner && (
              <button type="button" className="mlc-btn mlc-btn--ghost" style={{ height: 32, fontSize: 12 }} onClick={() => navigate('/app/messages')} data-testid="btn-contact-shipper">
                <MessageSquare size={13} /> Contacter
              </button>
            )}
          </div>
        )}
      </div>

      <div className="detail-actions">
        {isOwner && shipment.status === 'PUBLISHED' && (
          <>
            <button data-testid="btn-find-carrier" className="mlc-btn mlc-btn--primary" onClick={() => navigate(`/app/shipments/${id}/matching`)}>
              Trouver un transporteur <ArrowRight size={14} />
            </button>
            <button data-testid="btn-view-offers" className="mlc-btn mlc-btn--ghost" onClick={() => navigate(`/app/shipments/${id}/offers`)}>
              <Send size={14} /> Offres reçues
            </button>
            <button className="mlc-btn mlc-btn--ghost" onClick={() => navigate(`/app/shipments/${id}/edit`)} data-testid="btn-edit">
              <Pencil size={14} /> Modifier
            </button>
            <button className="mlc-btn mlc-btn--danger" onClick={handleCancel} data-testid="btn-cancel">
              <Ban size={14} /> Annuler
            </button>
            <button className="mlc-btn mlc-btn--danger" onClick={handleDelete} data-testid="btn-delete-shipment">
              <Trash2 size={14} /> Supprimer
            </button>
          </>
        )}
        {isOwner && shipment.status === 'PENDING_REVIEW' && (
          <>
            <button className="mlc-btn mlc-btn--ghost" onClick={() => navigate(`/app/shipments/${id}/edit`)} data-testid="btn-edit">
              <Pencil size={14} /> Modifier
            </button>
            <button className="mlc-btn mlc-btn--danger" onClick={handleCancel} data-testid="btn-cancel">
              <Ban size={14} /> Annuler
            </button>
            <button className="mlc-btn mlc-btn--danger" onClick={handleDelete} data-testid="btn-delete-shipment">
              <Trash2 size={14} /> Supprimer
            </button>
          </>
        )}
        {isOwner && shipment.status === 'IN_PROGRESS' && (
          <>
            <button className="mlc-btn mlc-btn--ghost" onClick={() => navigate('/app/messages')} data-testid="btn-contact">
              <MessageSquare size={14} /> Contacter le transporteur
            </button>
            <button className="mlc-btn mlc-btn--danger" onClick={() => toast.info('Bientôt disponible')} data-testid="btn-report">
              <AlertTriangle size={14} /> Signaler un problème
            </button>
          </>
        )}
        {!isOwner && (user?.roles || []).includes('ROLE_CARRIER') && shipment.status === 'PUBLISHED' && (() => {
          const negotiable = isNegotiable('carrier', shipment);
          return (
            <>
              <button
                data-testid="btn-propose-on-shipment"
                className="mlc-btn mlc-btn--primary"
                onClick={() => { setProposeMode('direct'); setProposeOpen(true); }}
              >
                <Send size={14} /> Faire une proposition
              </button>
              {negotiable ? (
                <button
                  data-testid="btn-negotiate-on-shipment"
                  className="mlc-btn mlc-btn--navy"
                  onClick={() => { setProposeMode('negotiation'); setProposeOpen(true); }}
                >
                  💬 Négocier
                </button>
              ) : (
                <button
                  data-testid="btn-non-negotiable-shipment"
                  className="mlc-btn mlc-btn--ghost"
                  disabled
                >
                  🔒 Non Négociable
                </button>
              )}
            </>
          );
        })()}
        {!isOwner && (
          <button data-testid="btn-contact-shipper-bar" className="mlc-btn mlc-btn--ghost" onClick={() => navigate('/app/messages')}>
            <MessageSquare size={14} /> Contacter
          </button>
        )}
      </div>

      <ProposeModal
        open={proposeOpen}
        onClose={() => { setProposeOpen(false); setProposeMode('direct'); }}
        meSide="carrier"
        target={shipment}
        mode={proposeMode}
        onSuccess={() => navigate('/app/propositions')}
      />

      <ConfirmDialog
        open={confirmDlg?.action === 'cancel'}
        onClose={() => setConfirmDlg(null)}
        onConfirm={doCancel}
        variant="destructive"
        icon="ban"
        title="Annuler cette annonce ?"
        subtitle="Elle sera conservée dans l'historique avec le statut Annulé."
        confirmLabel="Oui, annuler"
      />
      <ConfirmDialog
        open={confirmDlg?.action === 'delete'}
        onClose={() => setConfirmDlg(null)}
        onConfirm={doDelete}
        variant="destructive"
        icon="trash"
        title="Supprimer cette annonce ?"
        warn="Les propositions en attente seront également supprimées."
        confirmLabel="Supprimer définitivement"
      />
    </div>
  );
}

function InfoBlock({ icon, label, value }) {
  return (
    <div className="detail-info-block">
      <div className="detail-info-icon">{icon}</div>
      <div>
        <div className="detail-info-label">{label}</div>
        <div className="detail-info-value">{value}</div>
      </div>
    </div>
  );
}
